import React, { useState } from "react";
import { RegionMap, collectionFor } from "../components/RegionMap";
import { FestiveBanner } from "../components/FestiveBanner";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api, Collection, Product } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const collection = params.get("collection") || "";
  const page = Number(params.get("page") || 1);
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);

  const { data: collections } = useQuery<Collection[]>({ queryKey: ["collections"], queryFn: () => api.get("/catalog/collections").then((r) => r.data) });
  const { data: products, isLoading } = useQuery<Product[]>({
    queryKey: ["products", collection, page, q],
    queryFn: () => (q ? api.get("/catalog/search", { params: { q } }) : api.get("/catalog/products", { params: { collection: collection || undefined, page, limit: 24 } })).then((r) => r.data),
  });

  const featured = (collections || []).filter((c) => c.featured);
  const others = (collections || []).filter((c) => !c.featured);
  const current = collections?.find((c) => c.handle === collection);

  const go = (next: Record<string, string>) => {
    const p = new URLSearchParams();
    Object.entries({ collection, page: "1", q: "", ...next }).forEach(([k, v]) => v && v !== "1" && p.set(k, v));
    if (next.page && next.page !== "1") p.set("page", next.page);
    setParams(p);
  };

  return (
    <div className="space-y-10">
    <FestiveBanner compact />
    {!collection && !q && <RegionMap collections={collections || []} onPick={(r) => { const c = collectionFor(r, collections || []); if (c) { setSearch(""); go({ collection: c.handle, q: "" }); } else { setSearch(r); go({ q: r, collection: "" }); } }} />}
    <div className="grid gap-12 lg:grid-cols-[240px_1fr]" data-testid="shop-page">
      <aside className="space-y-8">
        <form onSubmit={(e) => { e.preventDefault(); go({ q: search, collection: "" }); }} className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={1.25} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search wines" className="rounded-none bg-transparent pl-9 pr-20" data-testid="shop-search-input" />
          <button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1 text-xs uppercase tracking-widest text-neutral-600 hover:text-[var(--wine)]" data-testid="shop-search-button">Search</button>
        </form>
        <div>
          <div className="eyebrow">Regions</div>
          <ul className="mt-3 space-y-2">
            <li><button onClick={() => { setSearch(""); go({ collection: "" }); }} className={`text-sm transition-colors duration-200 hover:text-[var(--wine)] ${!collection && !q ? "text-[var(--wine)]" : ""}`} data-testid="collection-all">All wines</button></li>
            {featured.map((c) => (
              <li key={c.handle}>
                <button onClick={() => { setSearch(""); go({ collection: c.handle }); }} className={`text-left text-sm transition-colors duration-200 hover:text-[var(--wine)] ${collection === c.handle ? "text-[var(--wine)]" : ""}`} data-testid={`collection-${c.handle}`}>
                  {c.title} {c.products_count ? <span className="text-neutral-400">· {c.products_count}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
        {others.length > 0 && (
          <div>
            <div className="eyebrow">Everything else</div>
            <select value={others.some((c) => c.handle === collection) ? collection : ""} onChange={(e) => { setSearch(""); go({ collection: e.target.value }); }} className="mt-3 w-full border border-border bg-transparent px-3 py-2 text-sm" data-testid="collection-select">
              <option value="">Choose a collection…</option>
              {others.map((c) => <option key={c.handle} value={c.handle}>{c.title}{c.products_count ? ` (${c.products_count})` : ""}</option>)}
            </select>
          </div>
        )}
      </aside>

      <section>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">{q ? "Search results" : "Collection"}</div>
            <h1 className="mt-2 font-serif text-4xl font-medium tracking-tight sm:text-5xl" data-testid="shop-title">{q ? `“${q}”` : current?.title || "All wines"}</h1>
          </div>
          {!q && (
            <div className="flex items-center gap-2 text-sm">
              <Button variant="outline" className="rounded-none" disabled={page <= 1} onClick={() => go({ page: String(page - 1) })} data-testid="shop-prev-page">Previous</Button>
              <span className="tabular-nums text-neutral-500" data-testid="shop-page-number">Page {page}</span>
              <Button variant="outline" className="rounded-none" disabled={!products || products.length < 24} onClick={() => go({ page: String(page + 1) })} data-testid="shop-next-page">Next</Button>
            </div>
          )}
        </div>
        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 xl:grid-cols-4" data-testid="product-grid">
          {isLoading && Array.from({ length: 8 }).map((_, i) => <Skeleton key={`skeleton-${i}`} className="aspect-[3/4] rounded-none bg-[var(--parchment)]" />)}
          {(products || []).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        {products && products.length === 0 && <p className="mt-10 text-sm text-neutral-500" data-testid="shop-empty">No wines found here.</p>}
      </section>
    </div>
    </div>
  );
}
